/**
 * System-index declarations — the single source of truth for the plain
 * btree indexes TypeGraph ships on its own relations.
 *
 * Both dialect schema factories (`createSqliteTables` /
 * `createPostgresTables`) derive their Drizzle index builders from this
 * list, so the two dialects cannot drift, and
 * `materializeSystemIndexes()` derives the same indexes as runtime DDL so
 * an index added in a new library version reaches already-initialized
 * databases (bootstrap DDL only runs on first boot — see
 * `loadActiveSchemaWithBootstrap`).
 *
 * Scope: non-unique btree column indexes on the four graph relations,
 * where performance iteration actually happens. Primary keys, unique /
 * partial-unique constraints, and status-table indexes are structural —
 * they are created with their table and never retrofitted — and stay in
 * the schema files.
 *
 * Changing a declaration's shape under the same suffix is a signature
 * drift: `materializeSystemIndexes` reports `failed` for it until the
 * operator drops the old physical index. Rename (new suffix) instead,
 * exactly like graph-declared indexes. Two acknowledged limits of that
 * contract: on databases whose indexes were bootstrap-created (no status
 * rows) an in-place reshape is undetectable at runtime — the rename rule
 * is enforced by review and the parity tests; and a renamed suffix
 * strands the old physical index (the runner only adds). When the first
 * rename actually happens, add a retired-suffixes list + drop path here.
 */
import { quoteIdentifier, shortHash } from "../query/dialect/vector-strategy";
import { requireDefined } from "../utils/presence";

/** The TypeGraph relations that carry system indexes. */
export type SystemIndexTable =
  "nodes" | "edges" | "recordedNodes" | "recordedEdges";

type LiveColumn =
  | "graph_id"
  | "kind"
  | "id"
  | "deleted_at"
  | "valid_from"
  | "valid_to"
  | "created_at";

type LiveEdgeColumn =
  LiveColumn | "from_kind" | "from_id" | "to_kind" | "to_id";

type RecordedColumn =
  | "graph_id"
  | "kind"
  | "id"
  | "valid_from"
  | "valid_to"
  | "recorded_from"
  | "recorded_to";

type RecordedEdgeColumn =
  RecordedColumn | "from_kind" | "from_id" | "to_kind" | "to_id";

type SystemIndexColumnsFor<T extends SystemIndexTable> =
  T extends "nodes" ? LiveColumn
  : T extends "edges" ? LiveEdgeColumn
  : T extends "recordedNodes" ? RecordedColumn
  : RecordedEdgeColumn;

type SystemIndexDeclarationFor<T extends SystemIndexTable> = Readonly<{
  table: T;
  /** Physical index name is `${physicalTableName}_${suffix}`. */
  suffix: string;
  columns: readonly [SystemIndexColumnsFor<T>, ...SystemIndexColumnsFor<T>[]];
}>;

export type SystemIndexDeclaration =
  | SystemIndexDeclarationFor<"nodes">
  | SystemIndexDeclarationFor<"edges">
  | SystemIndexDeclarationFor<"recordedNodes">
  | SystemIndexDeclarationFor<"recordedEdges">;

export const SYSTEM_INDEX_DECLARATIONS: readonly SystemIndexDeclaration[] = [
  // ---------------------------------------------------------- nodes
  { table: "nodes", suffix: "kind_idx", columns: ["graph_id", "kind"] },
  // Listing shape: kind partition ordered by creation with the
  // soft-delete column ahead of it so `deleted_at IS NULL` list queries
  // stay index-served.
  {
    table: "nodes",
    suffix: "kind_created_idx",
    columns: ["graph_id", "kind", "deleted_at", "created_at"],
  },
  {
    table: "nodes",
    suffix: "deleted_idx",
    columns: ["graph_id", "deleted_at"],
  },
  {
    table: "nodes",
    suffix: "valid_idx",
    columns: ["graph_id", "valid_from", "valid_to"],
  },
  // Bare-id lookup: the primary key leads with `kind` (graph_id, kind,
  // id), so a `WHERE graph_id = ? AND id = ?` probe that doesn't know the
  // kind can't seek it — SQLite falls back to a graph_id-only scan of
  // every node in the graph (~3M at LDBC SF1).
  // `store.algorithms.degree`'s node-kind subquery does exactly that
  // lookup (it resolves the seed's kind by id), so without this index
  // degree is a full nodes scan (~95ms at SF1). See typegraph#280.
  { table: "nodes", suffix: "id_idx", columns: ["graph_id", "id"] },

  // ---------------------------------------------------------- edges
  { table: "edges", suffix: "kind_idx", columns: ["graph_id", "kind"] },
  // Directional traversal index (outgoing): supports endpoint lookups
  // and extra filtering by edge kind / target kind. Includes every
  // system column the compiled query's soft-delete/temporal-validity
  // predicate touches (deleted_at, valid_from, valid_to), trailed by
  // to_id — the compiled traversal join reads `n.id = e.to_id` for an
  // outgoing traversal (standard-builders.ts), so without to_id here
  // the join still fetches the edge's heap row for that one column
  // even with the seek/predicate columns covered.
  {
    table: "edges",
    suffix: "from_idx",
    columns: [
      "graph_id",
      "from_kind",
      "from_id",
      "kind",
      "to_kind",
      "deleted_at",
      "valid_from",
      "valid_to",
      "to_id",
    ],
  },
  // Directional traversal index (incoming): mirrors from_idx for
  // reverse traversals, trailed by from_id for the same reason
  // (`n.id = e.from_id` for an incoming traversal).
  {
    table: "edges",
    suffix: "to_idx",
    columns: [
      "graph_id",
      "to_kind",
      "to_id",
      "kind",
      "from_kind",
      "deleted_at",
      "valid_from",
      "valid_to",
      "from_id",
    ],
  },
  {
    table: "edges",
    suffix: "kind_created_idx",
    columns: ["graph_id", "kind", "deleted_at", "created_at"],
  },
  {
    table: "edges",
    suffix: "deleted_idx",
    columns: ["graph_id", "deleted_at"],
  },
  {
    table: "edges",
    suffix: "valid_idx",
    columns: ["graph_id", "valid_from", "valid_to"],
  },
  // Cardinality enforcement: the one-per-endpoint check probes for a
  // currently-valid edge of a kind from a specific endpoint.
  {
    table: "edges",
    suffix: "cardinality_idx",
    columns: ["graph_id", "kind", "from_kind", "from_id", "valid_to"],
  },

  // ---------------------------------------------------------- recordedNodes
  {
    table: "recordedNodes",
    suffix: "entity_idx",
    columns: ["graph_id", "kind", "id", "recorded_from", "recorded_to"],
  },
  {
    table: "recordedNodes",
    suffix: "open_idx",
    columns: ["graph_id", "recorded_to"],
  },
  {
    table: "recordedNodes",
    suffix: "valid_idx",
    columns: ["graph_id", "valid_from", "valid_to"],
  },
  // Bare-id lookup parity with the live nodes table: recorded-pinned
  // reads swap this relation in as the node source, and `entity_idx`
  // leads with `kind`, so the same kind-by-bare-id probe (e.g.
  // `degree()` at a recorded coordinate) would otherwise scan every
  // historical version in the graph. See typegraph#280.
  { table: "recordedNodes", suffix: "id_idx", columns: ["graph_id", "id"] },
  // `recorded_from`-led lookup: `entity_idx` leads with `kind, id`, so it
  // cannot serve the lineage capability's changed-since scan
  // (`store/recorded-capture/lineage.ts`), which filters by `graph_id` and
  // `recorded_from` alone across every kind. Without this the scan is a
  // full per-graph table scan of every historical row.
  {
    table: "recordedNodes",
    suffix: "since_idx",
    columns: ["graph_id", "recorded_from"],
  },

  // ---------------------------------------------------------- recordedEdges
  {
    table: "recordedEdges",
    suffix: "entity_idx",
    columns: ["graph_id", "kind", "id", "recorded_from", "recorded_to"],
  },
  {
    table: "recordedEdges",
    suffix: "open_idx",
    columns: ["graph_id", "recorded_to"],
  },
  {
    table: "recordedEdges",
    suffix: "from_idx",
    columns: [
      "graph_id",
      "from_kind",
      "from_id",
      "kind",
      "to_kind",
      "recorded_from",
      "recorded_to",
    ],
  },
  {
    table: "recordedEdges",
    suffix: "to_idx",
    columns: [
      "graph_id",
      "to_kind",
      "to_id",
      "kind",
      "from_kind",
      "recorded_from",
      "recorded_to",
    ],
  },
  {
    table: "recordedEdges",
    suffix: "valid_idx",
    columns: ["graph_id", "valid_from", "valid_to"],
  },
  // `recorded_from`-led lookup, same rationale as recordedNodes' since_idx:
  // the directional indexes above lead with an endpoint, not `recorded_from`
  // alone, so they cannot serve the lineage capability's changed-since scan.
  {
    table: "recordedEdges",
    suffix: "since_idx",
    columns: ["graph_id", "recorded_from"],
  },
];

/** Default physical table names for the system-index relations. */
const DEFAULT_SYSTEM_INDEX_TABLE_NAMES: Readonly<
  Record<SystemIndexTable, string>
> = {
  nodes: "typegraph_nodes",
  edges: "typegraph_edges",
  recordedNodes: "typegraph_recorded_nodes",
  recordedEdges: "typegraph_recorded_edges",
};

/**
 * Resolves the physical table name for a system-index relation, honoring
 * a backend's table-name overrides.
 */
export function resolveSystemIndexTableName(
  table: SystemIndexTable,
  overrides:
    Readonly<Partial<Record<SystemIndexTable, string | undefined>>> | undefined,
): string {
  return overrides?.[table] ?? DEFAULT_SYSTEM_INDEX_TABLE_NAMES[table];
}

/**
 * PostgreSQL truncates identifiers to NAMEDATALEN-1 = 63 bytes SILENTLY —
 * two suffixes of one long custom table name would collide after
 * truncation, and the catalog probes (which compare by exact name) would
 * never match the stored name, re-attempting the build on every boot.
 * SQLite has no such limit but uses the same bound so the two dialects'
 * index sets stay in byte-for-byte parity. (Char-based like the vector
 * identifier bound — multibyte table names are out of scope.)
 */
const MAX_SQL_IDENTIFIER_LENGTH = 63;

/**
 * Physical index name: `${physicalTableName}_${suffix}`, deterministically
 * truncated + hash-suffixed past the 63-char identifier bound so distinct
 * indexes stay distinct. Single choke point — the Drizzle builders, the
 * runtime DDL, the catalog probes, and the status keys all derive the
 * name here, so they can never disagree.
 */
export function systemIndexName(
  physicalTableName: string,
  suffix: string,
): string {
  const full = `${physicalTableName}_${suffix}`;
  if (full.length <= MAX_SQL_IDENTIFIER_LENGTH) return full;
  const hash = shortHash(JSON.stringify([physicalTableName, suffix]));
  return `${full.slice(0, MAX_SQL_IDENTIFIER_LENGTH - 1 - hash.length)}_${hash}`;
}

/**
 * Every system index name for a deployment's (possibly overridden) table
 * names. Graph-declared index names must not collide with these: a
 * colliding `CREATE INDEX IF NOT EXISTS` would silently no-op against the
 * system index (wrong shape) while recording a false success — see the
 * guards in the schema factories and `materializeIndexes`.
 */
export function resolveSystemIndexNames(
  overrides:
    Readonly<Partial<Record<SystemIndexTable, string | undefined>>> | undefined,
): ReadonlySet<string> {
  return new Set(
    SYSTEM_INDEX_DECLARATIONS.map((declaration) =>
      systemIndexName(
        resolveSystemIndexTableName(declaration.table, overrides),
        declaration.suffix,
      ),
    ),
  );
}

/**
 * Rejects graph-declared indexes whose names collide with this
 * deployment's system index names. Called by both dialect schema
 * factories so a colliding declaration fails at table-definition time
 * instead of silently no-opping against the (differently shaped) system
 * index at CREATE INDEX IF NOT EXISTS time. `materializeIndexes` applies
 * the same check per-declaration on its runtime path.
 */
export function assertNoSystemIndexNameCollision(
  indexes: readonly Readonly<{ name: string }>[],
  overrides:
    Readonly<Partial<Record<SystemIndexTable, string | undefined>>> | undefined,
): void {
  if (indexes.length === 0) return;
  const reserved = resolveSystemIndexNames(overrides);
  for (const index of indexes) {
    if (reserved.has(index.name)) {
      throw new Error(
        `Index name "${index.name}" collides with a TypeGraph system ` +
          `index. Choose a different name — system index names are ` +
          `reserved, and a same-named CREATE INDEX IF NOT EXISTS would ` +
          `silently no-op against the system index instead of creating ` +
          `the declared one.`,
      );
    }
  }
}

/**
 * Runtime DDL for one system index — byte-compatible with the index the
 * bootstrap path creates from the Drizzle schema (same name, same
 * columns), plus `CONCURRENTLY` where the dialect supports building
 * without blocking writes.
 */
export function generateSystemIndexDDL(
  declaration: SystemIndexDeclaration,
  physicalTableName: string,
  options: Readonly<{ concurrent: boolean }>,
): string {
  const name = quoteIdentifier(
    systemIndexName(physicalTableName, declaration.suffix),
  );
  const table = quoteIdentifier(physicalTableName);
  const columns = declaration.columns
    .map((column) => quoteIdentifier(column))
    .join(", ");
  const concurrently = options.concurrent ? "CONCURRENTLY " : "";
  return `CREATE INDEX ${concurrently}IF NOT EXISTS ${name} ON ${table} (${columns});`;
}

/**
 * Runtime DDL for the base-schema release's three `since_idx (graph_id,
 * recorded_from)` indexes — the lineage capability's changed-since scan
 * (`store/recorded-capture/lineage.ts`): the two recorded relations
 * (`recordedNodes`, `recordedEdges`, declared in
 * {@link SYSTEM_INDEX_DECLARATIONS} and managed by `materializeIndexes`),
 * plus the recorded identity-assertions relation's own `since_idx`, which
 * `changesSince`'s per-revision completeness scan folds into the same
 * three-relation scan but which is NOT a {@link SystemIndexTable} — like that
 * relation's other three indexes (`entity_idx`/`a_idx`/`b_idx`), it is
 * structural, hand-declared with the table rather than run through
 * `materializeIndexes`' lazy backfill, and this function follows the same
 * pattern for it. Both dialect factories thread this into the base-schema
 * version-3 adoption step (`engine/members/base-schema-members.ts`) the
 * same way `fencesTableDdl` feeds version 2: a fresh bootstrap already
 * carries all three indexes through the schema factories' own index
 * builders, but a database that reaches adoption without re-running
 * bootstrap DDL (a reopen of an already-provisioned installation) needs
 * this explicit `CREATE INDEX IF NOT EXISTS` triple. The adoption step must
 * first ensure recorded identity-assertions storage: older provisioned
 * installations can predate that table, which originally shipped only in
 * bootstrap DDL. One owner for the
 * declaration lookup and the identity-assertions index's own name (both
 * routed through {@link systemIndexName}, the single naming choke point)
 * keeps the two dialect factories from re-spelling either and risking
 * drift between them; the identity-assertions name this renders MUST match
 * the schema factories' own hand-written `index(...)` call byte-for-byte —
 * the shape-ratchet test (`tests/base-schema-shape-ratchet.test.ts`) is
 * what would catch a drift.
 */
export function sinceIndexAdoptionDdl(
  recordedTableNames: Readonly<{
    recordedNodes: string;
    recordedEdges: string;
    recordedIdentityAssertions: string;
  }>,
): readonly [string, string, string] {
  function sinceIndexDdlFor(table: "recordedNodes" | "recordedEdges"): string {
    const declaration = requireDefined(
      SYSTEM_INDEX_DECLARATIONS.find(
        (candidate) =>
          candidate.table === table && candidate.suffix === "since_idx",
      ),
      `No "since_idx" system index is declared for "${table}".`,
    );
    return generateSystemIndexDDL(declaration, recordedTableNames[table], {
      concurrent: false,
    });
  }
  function identityAssertionsSinceIndexDdl(): string {
    const name = quoteIdentifier(
      systemIndexName(
        recordedTableNames.recordedIdentityAssertions,
        "since_idx",
      ),
    );
    const table = quoteIdentifier(
      recordedTableNames.recordedIdentityAssertions,
    );
    return `CREATE INDEX IF NOT EXISTS ${name} ON ${table} (${quoteIdentifier("graph_id")}, ${quoteIdentifier("recorded_from")});`;
  }
  return [
    sinceIndexDdlFor("recordedNodes"),
    sinceIndexDdlFor("recordedEdges"),
    identityAssertionsSinceIndexDdl(),
  ];
}
